/**
 * Every non-DOM decision the social sign-in composable makes about what came back
 * from the server lives here, as pure functions over plain data.
 *
 * The refusal codes are the contract in `docs/initiatives/social-sign-in/overview.md`
 * ("Library endpoints"). The policy hook on the backend answers with
 * `{ code, message, email? }`; the client never re-derives a rule, it only maps.
 */

import type { IdentityDto } from '@saas/api-client';

import type { AuthClientUser } from '@/lib/auth-client';

/** The providers this build can name. The type is derived, so the list is the contract. */
export const SOCIAL_PROVIDERS = ['google', 'apple', 'microsoft'] as const;
export type SocialProvider = (typeof SOCIAL_PROVIDERS)[number];

/** The provider's display name, as the buttons, rows and notices say it. */
export const SOCIAL_PROVIDER_LABEL: Record<SocialProvider, string> = {
	google: 'Google',
	apple: 'Apple',
	microsoft: 'Microsoft',
};

/**
 * Every named provider has a button and an SDK on web, so naming one and being able to start one
 * are the same question here. Whether a deployment actually offers it is the other half, and
 * `isOfferedProvider` in `config.ts` is the seam every surface asks. (Mobile keeps its own list,
 * where Microsoft is named but not startable, so the distinction is still live there.)
 */
export const isNamedProvider = (provider: string): provider is SocialProvider =>
	SOCIAL_PROVIDERS.some((named) => named === provider);

/** An identity row for a provider this build can drive, which is what every row action needs. */
export type DrivableIdentity = IdentityDto & { provider: SocialProvider };

/**
 * Why a tap ended badly. Each one has its own message on the calling screen.
 *
 * Kept identical to the union in `apps/mobile/src/screens/auth/social/outcome.ts`, so a screen
 * ported between the apps keeps every branch. Mobile never produces `unavailable`,
 * `in-app-browser` or `popup-blocked`; those are how a browser fails to reach the provider at all.
 * Nor `org-blocked`, until mobile offers Microsoft.
 */
export type SocialErrorReason =
	| 'provider'
	| 'network'
	| 'popup-blocked'
	| 'inactive'
	| 'deleted'
	/** The token was already spent or no longer verifies; a second tap always helps. */
	| 'expired'
	/** The server refused to link this provider to this account; the password still works. */
	| 'not-allowed'
	/** The provider's script never loaded or its button never mounted. */
	| 'unavailable'
	/** The page is inside an embedded webview, where the providers refuse OAuth. */
	| 'in-app-browser'
	/** The person's Microsoft tenant refuses third-party apps; only their IT admin can undo it. */
	| 'org-blocked'
	/** The server answered 5xx with no refusal code; retrying in a moment may help. */
	| 'server';

/** The account the exchange signed in, narrowed to what routing needs. */
export interface SocialUser {
	id: string;
	email: string;
	name: string;
	/** Set while the signup has not been finished on the details step, null once it has. */
	incompleteSince: string | null;
}

/** What a retry after a mailbox proof needs to repeat the exact same exchange. */
export interface SocialRetry {
	provider: SocialProvider;
	idToken: string;
	nonce: string;
}

export type SocialOutcome =
	/** Signed in and complete: the caller stores the token and routes into the app. */
	| { kind: 'signed-in'; token: string; user: SocialUser }
	/** Signed in but the signup is unfinished: the caller routes to the details step. */
	| { kind: 'incomplete'; token: string; user: SocialUser }
	/** `intent: 'connect'` succeeded. `/link-social` answers with no token and no user. */
	| { kind: 'linked'; provider: SocialProvider }
	/** An existing User owns this address but never proved the mailbox; prove it, then retry. */
	| { kind: 'proof-required'; email: string; retry: SocialRetry }
	/** The provider would not vouch for the address; fall back to an emailed code. */
	| { kind: 'unverified'; email: string }
	/** The token carried no address at all, so there is nothing to prefill: ask for one. */
	| { kind: 'email-missing' }
	/** The identity is already attached to a different account (connect only). */
	| { kind: 'attached-elsewhere' }
	/** The account picker was closed without choosing. Silent by design. */
	| { kind: 'dismissed' }
	| { kind: 'error'; reason: SocialErrorReason };

/** A refusal as it reaches us, before any product meaning is attached to it. */
export interface SocialRefusal {
	code: string | null;
	status: number | null;
	email: string | null;
	retry: SocialRetry;
}

/**
 * The whole code-to-outcome table. Anything unrecognised is a provider error, so a
 * refusal this build has not heard of degrades to "sign-in did not complete" rather
 * than to a blank screen.
 */
export const mapRefusalToOutcome = (refusal: SocialRefusal): SocialOutcome => {
	switch (refusal.code) {
		case 'PROVIDER_EMAIL_UNVERIFIED':
			return refusal.email ? { kind: 'unverified', email: refusal.email } : { kind: 'error', reason: 'provider' };
		case 'PROVIDER_EMAIL_MISSING':
			// The token carries no email claim, so the refusal echoes no address and a second tap
			// would carry the same token: the only way on is to type an address.
			return { kind: 'email-missing' };
		case 'MAILBOX_PROOF_REQUIRED':
			return refusal.email
				? { kind: 'proof-required', email: refusal.email, retry: refusal.retry }
				: { kind: 'error', reason: 'provider' };
		case 'IDENTITY_ATTACHED_ELSEWHERE':
			return { kind: 'attached-elsewhere' };
		case 'ACCOUNT_INACTIVE':
			return { kind: 'error', reason: 'inactive' };
		case 'ACCOUNT_DELETED':
			return { kind: 'error', reason: 'deleted' };
		case 'TOKEN_REPLAYED':
		case 'INVALID_TOKEN':
			return { kind: 'error', reason: 'expired' };
		case 'LINK_REFUSED':
			return { kind: 'error', reason: 'not-allowed' };
		case 'NONCE_REQUIRED':
			// Only a client bug sends a token without a nonce; a retry does not help and the copy
			// must not promise that it does.
			return { kind: 'error', reason: 'provider' };
		default:
			return { kind: 'error', reason: unknownRefusalReason(refusal.status) };
	}
};

/**
 * A refusal with no code this build knows is read from its status alone: no status means the
 * request never reached a server, 5xx means the server broke, and anything else is the provider.
 */
const unknownRefusalReason = (status: number | null): SocialErrorReason => {
	if (status === null) return 'network';
	if (status >= 500) return 'server';
	return 'provider';
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/** Reads one string off an untyped payload, or null when it is absent or the wrong type. */
export const readStringField = ({ source, key }: { source: unknown; key: string }): string | null => {
	if (!isRecord(source)) return null;
	const value = source[key];
	return typeof value === 'string' ? value : null;
};

/**
 * The account row as the auth client hands it back, narrowed to what routing reads.
 *
 * `lib/auth-client.ts` declares the server's `user.additionalFields`, so `incompleteSince` arrives
 * typed - as a real `Date`, because the client's response parser revives every ISO-shaped string
 * into one. Picking the fields off the client's own type rather than re-describing them is what
 * makes a dropped declaration a typecheck failure here instead of a screen silently reading an
 * untyped payload again.
 */
export type SocialUserRow = Pick<AuthClientUser, 'id' | 'email' | 'name' | 'incompleteSince'>;

/** With the fields typed there is nothing left to parse: an absent user is the only bad case. */
export const parseSocialUser = (value: SocialUserRow | null | undefined): SocialUser | null => {
	if (!value) return null;
	return {
		id: value.id,
		email: value.email,
		name: value.name,
		incompleteSince: value.incompleteSince ? value.incompleteSince.toISOString() : null,
	};
};

/** A successful exchange, as the library hands it back. */
export interface SocialSuccess {
	token: string | null;
	user: SocialUserRow | null | undefined;
}

/** An unfinished signup is the only thing that separates the two success outcomes. */
export const mapSuccessToOutcome = (success: SocialSuccess): SocialOutcome => {
	const user = parseSocialUser(success.user);
	if (!success.token || !user) return { kind: 'error', reason: 'provider' };
	return user.incompleteSince
		? { kind: 'incomplete', token: success.token, user }
		: { kind: 'signed-in', token: success.token, user };
};
